import { type ReactNode, useEffect, useRef } from 'react'

// Animates its own height between renders whenever `trigger` changes. The
// content swaps instantly; the box height tweens from the previous measured
// height to the new one via the Web Animations API (temporary override, so no
// state or 'auto' juggling). Overflow is clipped during the tween.
export function AnimatedHeight({ children }: { children: ReactNode }) {
  const outer = useRef<HTMLDivElement>(null)
  const inner = useRef<HTMLDivElement>(null)

  // A single ResizeObserver on the inner content is the *only* driver: whenever the
  // natural content height changes — the toggle, the grid's late column re-measure,
  // any async settle — we tween the clipping box to match. One code path means the
  // tween can never land on a stale pre-settle height (the old "expand gets stuck
  // until you tap" bug came from a separate layout-effect measurement racing the RO).
  useEffect(() => {
    const outerEl = outer.current
    const innerEl = inner.current
    if (!outerEl || !innerEl) return
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    outerEl.style.height = '' // rest at auto; wipe any stale inline height (e.g. HMR)
    let ready = false // have we taken a baseline measurement to animate from?
    let anim: Animation | null = null

    const apply = () => {
      const to = innerEl.offsetHeight
      if (!ready) {
        ready = true // first measure: nothing to tween from, just adopt the height
        return
      }
      // Never freeze an inline height on the box — it rests at height:auto and so
      // always hugs the content exactly (freezing a px value clips if it's a hair
      // short of the settled height). getBoundingClientRect reads the live height
      // both at rest (== content) and mid-tween, so re-targets stay smooth.
      const from = outerEl.getBoundingClientRect().height
      if (reduce || Math.round(from) === Math.round(to)) return
      anim?.cancel() // stop the old tween fighting the new one
      const a = outerEl.animate([{ height: `${from}px` }, { height: `${to}px` }], {
        // Quick single-direction ease-out (easeOutQuint) — snappier than `ease`.
        duration: 190,
        easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
      })
      // fill defaults to `none`, so on finish the box reverts to height:auto and
      // settles exactly on the content — no clipping, no leftover inline height.
      anim = a
      const clear = () => {
        if (anim === a) anim = null
      }
      a.onfinish = clear
      a.oncancel = clear
    }

    // RO callbacks fire between layout and paint, so the tween is armed before the
    // browser ever paints the jumped-to height — no flash.
    const ro = new ResizeObserver(apply)
    ro.observe(innerEl)
    apply() // take the baseline now so the first real change tweens, not snaps
    return () => {
      ro.disconnect()
      anim?.cancel()
    }
  }, [])

  return (
    // `contain` scopes layout/paint so many cards animating at once don't each
    // thrash the page's layout.
    <div ref={outer} style={{ overflow: 'hidden', contain: 'layout paint' }}>
      <div ref={inner}>{children}</div>
    </div>
  )
}
